import { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminConsoleUsersApi } from '../../../api/adminConsole';

export type StaffUserOption = { id: string; email: string; displayName: string };

function label(o: StaffUserOption): string {
  return o.displayName && o.displayName !== o.email ? `${o.displayName} (${o.email})` : o.email;
}

function useSearchResults(input: string, enabled: boolean) {
  const [q, setQ] = useState(input);
  useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);
  return useQuery({
    queryKey: ['adminStaffUserPicker', q],
    queryFn: () => adminConsoleUsersApi.list({ q: q || undefined, status: 'active', limit: 25, sort: 'email:asc' }),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** Single staff user, searched server-side (`GET /admin/users?q=`). */
export function StaffUserPicker({
  label: fieldLabel,
  value,
  onChange,
  excludeIds = [],
  helperText,
  disabled,
}: {
  label: string;
  value: StaffUserOption | null;
  onChange: (v: StaffUserOption | null) => void;
  excludeIds?: readonly string[];
  helperText?: string;
  disabled?: boolean;
}) {
  const [input, setInput] = useState('');
  const results = useSearchResults(input, !disabled);
  const options = (results.data?.items ?? [])
    .filter((u) => !excludeIds.includes(u.id))
    .map((u) => ({ id: u.id, email: u.email, displayName: u.displayName }));
  return (
    <Autocomplete
      size="small"
      options={options}
      value={value}
      disabled={disabled}
      onChange={(_, v) => onChange(v)}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      getOptionLabel={label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      loading={results.isFetching}
      renderInput={(params) => (
        <TextField
          {...params}
          label={fieldLabel}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {results.isFetching ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

/** Several staff users. */
export function StaffUsersMultiPicker({
  label: fieldLabel,
  value,
  onChange,
  helperText,
}: {
  label: string;
  value: StaffUserOption[];
  onChange: (v: StaffUserOption[]) => void;
  helperText?: string;
}) {
  const [input, setInput] = useState('');
  const results = useSearchResults(input, true);
  const options = (results.data?.items ?? []).map((u) => ({ id: u.id, email: u.email, displayName: u.displayName }));
  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        if (reason !== 'reset') setInput(v);
      }}
      getOptionLabel={label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      loading={results.isFetching}
      renderInput={(params) => <TextField {...params} label={fieldLabel} helperText={helperText} />}
    />
  );
}
